import { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { notifyError, notifyInfo, notifySuccess } from "components/ToastMessage";
import { useDispatch, useSelector } from "react-redux";
import { addDomainToCart } from "features/common/layouts/cartSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faPlus, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { formatIDR } from "helpers/utils";
import { search, searchDomainName } from "features/domain/domainsSlice";
import { hasFreeDomainInCart, getIsFreeDomain as getIsFreeDomainHelper } from "helpers/helpersSuccessDomain";

const DomainSuggestions = ({ unavailableDomain, extensions = [] }) => {
  const dispatch = useDispatch();
  const { productsList: cartItems } = useSelector((state) => state.cart);
  const { domain_register: results } = useSelector((state) => state.domain);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableTlds, setAvailableTlds] = useState([]);
  const [domainPrices, setDomainPrices] = useState({}); // Store prices for each domain
  const [tldPromoData, setTldPromoData] = useState({}); // Store TLD promo data
  const [showSuggestions, setShowSuggestions] = useState(true); // Toggle untuk show/hide suggestions
  const [extensionPrices, setExtensionPrices] = useState({}); // Store extension prices for duration options
  const [durations, setDurations] = useState({}); // Store selected duration for each suggestion

  // Hardcode TLD untuk sementara (bisa dialihkan kembali ke fetch query)
  const USE_HARDCODED_TLDS = true; // Set to false untuk kembali ke fetch query
  const HARDCODED_TLDS = ["com", "shop", "id", "net", "co.id"]; // TLD yang di-hardcode untuk NameStudio

  const API_URL = `${process.env.REACT_APP_BASE_API}order/apis/getDomainSuggestions.php`;
  const OPENAI_TLD_API_URL = `${process.env.REACT_APP_BASE_API}order/apis/getOpenAITldSuggestions.php`;

  // Fetch all available TLDs from API
  useEffect(() => {
    // Jika menggunakan hardcode TLD, langsung set tanpa fetch
    if (USE_HARDCODED_TLDS) {
      //   console.log("Using hardcoded TLDs for NameStudio:", HARDCODED_TLDS);
      setAvailableTlds(HARDCODED_TLDS);
      return;
    }

    // Jika tidak, fetch dari API seperti biasa
    const fetchTlds = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_QWORDS_API}api/domain.php?_type=query`);
        const data = await response.json();

        const tlds = [];
        if (Array.isArray(data)) {
          data.forEach((item) => {
            if (Array.isArray(item) && item.length > 1) {
              const extension = item[1];
              if (extension && typeof extension === 'string') {
                const tld = extension.replace(".", "").toLowerCase();
                if (tld && tld.length > 0 && !tlds.includes(tld)) {
                  tlds.push(tld);
                }
              }
            }
          });
        }

        setAvailableTlds(tlds.length > 0 ? tlds : ["com", "net", "org", "id"]);
      } catch (error) {
        console.error("Error fetching TLDs:", error);
        setAvailableTlds(["com", "net", "org", "id"]);
      }
    };

    fetchTlds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate suggestions when domain is unavailable
  useEffect(() => {
    let timeoutId;

    if (unavailableDomain && availableTlds.length > 0) {
      // Reset suggestions first
      setSuggestions([]);
      setLoading(false);

      // Debounce to prevent multiple calls when dependencies change rapidly
      timeoutId = setTimeout(() => {
        generateSuggestions();
      }, 300);
    }

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unavailableDomain, availableTlds]);

  // Get recommended TLDs from OpenAI based on domain base name
  const getRecommendedTlds = async (baseName, availableTldsList) => {
    if (!baseName.trim() || availableTldsList.length === 0) {
      return [];
    }

    try {
      const response = await fetch(OPENAI_TLD_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          description: baseName.trim(),
          availableTlds: availableTldsList,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.recommendedTlds && data.recommendedTlds.length > 0) {
        return data.recommendedTlds;
      } else {
        // If OpenAI doesn't return recommendations, return empty array
        // This will allow NameStudio to use all available TLDs or its own selection
        return [];
      }
    } catch (error) {
      console.error("Error getting recommended TLDs:", error);
      // If OpenAI fails, return empty array
      // This will allow NameStudio to use all available TLDs or its own selection
      return [];
    }
  };

  const generateSuggestions = async () => {
    if (!unavailableDomain) return;

    // Prevent multiple simultaneous calls
    if (loading) {
      //   console.log("Already generating suggestions, skipping...");
      return;
    }

    setLoading(true);
    setSuggestions([]);

    try {
      // Extract base name without TLD
      const baseName = unavailableDomain.split('.')[0];

      // Use all available TLDs (no exclusion of original TLD)
      if (availableTlds.length === 0) {
        notifyError("Tidak ada TLD yang tersedia.");
        setLoading(false);
        return;
      }

      // Jika menggunakan hardcode TLD, langsung gunakan hardcode TLD tanpa OpenAI
      let finalRecommendedTlds;
      if (USE_HARDCODED_TLDS) {
        // Gunakan hardcode TLD langsung untuk NameStudio
        finalRecommendedTlds = HARDCODED_TLDS;
        // console.log("Using hardcoded TLDs for NameStudio:", finalRecommendedTlds);
        // notifyInfo(`Mencari domain tersedia dengan TLD: ${finalRecommendedTlds.map(tld => '.' + tld).join(', ')}`);
      } else {
        // Get recommended TLDs from OpenAI based on base name
        notifyInfo("Menganalisis domain dan memilih TLD yang relevan...");
        const recommendedTlds = await getRecommendedTlds(baseName, availableTlds);

        // Filter recommended TLDs to ensure they're in available list
        const filteredRecommendedTlds = recommendedTlds
          .filter(tld => {
            const tldClean = tld.replace(/^\./, '').toLowerCase();
            return availableTlds.some(availableTld => {
              const availableTldClean = availableTld.replace(/^\./, '').toLowerCase();
              return availableTldClean === tldClean;
            });
          })
          .slice(0, 5);

        if (filteredRecommendedTlds.length === 0) {
          // If no recommendations from OpenAI, let NameStudio use all available TLDs
          // Don't send tlds field, let NameStudio decide
          finalRecommendedTlds = [];

          notifyInfo(`Mencari domain tersedia yang mungkin cocok untuk anda`);
        } else {
          finalRecommendedTlds = filteredRecommendedTlds;
          notifyInfo(`TLD yang dipilih oleh AI: ${finalRecommendedTlds.map(tld => '.' + tld).join(', ')}`);
        }
      }

      // Create prompt in Indonesian that asks for related alternatives
      // Context: User searched for this specific domain, find alternatives with related theme/words
      // Analyze the base name to understand its components (e.g., "indonesiajaya" = "indonesia" + "jaya")
      const description = `Pengguna mencari domain "${unavailableDomain}" (nama: "${baseName}") tetapi domain tersebut tidak tersedia. Analisis dan pecah kata "${baseName}" menjadi komponen-komponennya dalam bahasa Indonesia (contoh: "indonesiajaya" = "indonesia" (negara) + "jaya" (kejayaan/kemenangan)). Carikan alternatif nama domain yang terkait dengan tema atau makna dari komponen-komponen tersebut menggunakan sinonim, variasi kata, atau kombinasi kata baru yang relevan. Berikan saran yang BENAR-BENAR BERBEDA dari "${baseName}" tetapi tetap terkait dengan tema komponen-komponennya.`;

      const requestBody = {
        "ai-mode": "always",
        "sensitive-content-filter": "true",
        "include-registered": "false",
        "max-length": 63,
        "max-results": 20, // Maximum allowed by NameStudio API
        "include-suggestion-type": true,
        messages: [
          {
            role: "user",
            message: description,
          },
        ],
        choices: {},
        lang: "IND",
        // Only include tlds if we have recommendations, otherwise let NameStudio decide
        ...(finalRecommendedTlds.length > 0 ? { tlds: finalRecommendedTlds } : {}),
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // If limit exceeded or empty response, hide suggestions
        if (errorData.limit_exceeded || errorData.empty_response) {
          setSuggestions([]);
          setLoading(false);
          return; // Exit silently, don't show error
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        // If limit exceeded or empty response, hide suggestions
        if (responseData.limit_exceeded || responseData.empty_response) {
          setSuggestions([]);
          setLoading(false);
          return; // Exit silently, don't show error
        }
        throw new Error(responseData.error || "API request failed");
      }

      const data = responseData.data || responseData;

      // Handle response format from NameStudio API
      let suggestionsList = [];

      if (data.results && Array.isArray(data.results)) {
        // Filter only available domains - this is mandatory
        suggestionsList = data.results.filter(item => {
          // Ensure availability is exactly "available"
          return item.availability === "available" || item.availability === "Available";
        });
      } else if (data.suggestions && Array.isArray(data.suggestions)) {
        suggestionsList = data.suggestions.filter(item =>
          item.availability === "available" || item.availability === "Available"
        );
      } else if (Array.isArray(data)) {
        suggestionsList = data.filter(item =>
          (item.availability === "available" || item.availability === "Available") ||
          (item.status === "available")
        );
      }

      // Debug: Log suggestions list
      // console.log("NameStudio API Response - suggestionsList:", suggestionsList.length, suggestionsList);

      // If no suggestions found, hide the section
      if (suggestionsList.length === 0) {
        console.warn("No suggestions found in NameStudio API response:", data);
        setSuggestions([]);
        setLoading(false);
        return; // Exit silently, don't show error
      }

      // Map to expected format and filter by different words
      const baseNameLower = baseName.toLowerCase();
      // Split base name by common separators and also try to detect compound words
      // For compound words like "indonesiajaya", we'll be more lenient in filtering
      const baseWords = baseNameLower.split(/[\s\-_]+/).filter(w => w.length > 2);
      const isCompoundWord = baseWords.length === 0 && baseNameLower.length > 6; // Likely a compound word if no separators
      
      // Debug: Log base name analysis
      // console.log("Base name analysis:", { baseName, baseNameLower, baseWords, isCompoundWord });

      const mappedSuggestions = suggestionsList
        .map((item) => {
          const domain = item.name || item.domain || item;
          return {
            domain: typeof domain === 'string' ? domain : (domain.name || domain),
            suggestionType: item.sldSuggestionType || item.suggestionType || item.type || null,
            availability: item.availability || "available",
          };
        })
        .filter((suggestion) => {
          // Only show available domains
          if (suggestion.availability !== "available" && suggestion.availability !== "Available") {
            return false;
          }

          const suggestionBase = suggestion.domain.split('.')[0].toLowerCase();
          const suggestionWords = suggestionBase.split(/[\s\-_]+/).filter(w => w.length > 2);

          // Exclude if base name is exactly the same (only TLD different)
          if (suggestionBase === baseNameLower) {
            return false;
          }

          // For compound words (like "indonesiajaya"), be more lenient
          // Allow suggestions that contain parts of the compound word
          if (isCompoundWord) {
            // For compound words, only exclude if it's exactly the same (only TLD different)
            // This was already checked above, so if we reach here, it's different
            // Allow all variations for compound words as they are likely related
            return true;
          }

          // For single word domains (with separators), be more lenient
          if (baseWords.length === 0 || suggestionWords.length === 0) {
            // Single word domain - use Levenshtein distance only
            const similarity = calculateSimilarity(suggestionBase, baseNameLower);
            // Only exclude if similarity is very high (> 0.9) - more lenient
            if (similarity >= 0.9) {
              return false;
            }
            return true;
          }

          // For multi-word domains, check word similarity
          if (baseWords.length > 0 && suggestionWords.length > 0) {
            const suggestionWordsSet = new Set(suggestionWords);
            const commonWords = baseWords.filter(w => suggestionWordsSet.has(w));
            const wordSimilarity = commonWords.length / Math.max(baseWords.length, suggestionWords.length);
            // Increase threshold to 80% for multi-word domains - more lenient
            if (wordSimilarity > 0.8) {
              return false;
            }
          }

          // Exclude if similarity is too high (Levenshtein distance)
          // Increase threshold to 0.85 for more suggestions - more lenient
          const similarity = calculateSimilarity(suggestionBase, baseNameLower);
          if (similarity >= 0.85) {
            return false;
          }

          // Exclude if it starts with the same prefix (only for longer domains)
          // More lenient - only check for very long domains
          const minLength = Math.min(suggestionBase.length, baseNameLower.length);
          if (minLength >= 12) {
            const prefixLength = Math.min(10, minLength);
            if (suggestionBase.substring(0, prefixLength) === baseNameLower.substring(0, prefixLength)) {
              return false;
            }
          }

          return true;
        });

      // Debug: Log mapped suggestions after filtering
      // console.log("Mapped suggestions after filtering:", mappedSuggestions.length, mappedSuggestions.map(s => s.domain));

      // Don't slice here - keep all filtered suggestions for maximum options

      // Group suggestions by TLD to ensure diversity
      // CRITICAL: Ensure each suggestion has a DIFFERENT TLD
      const suggestionsByTld = {};

      // Group all suggestions by TLD
      for (const suggestion of mappedSuggestions) {
        const suggestionTld = suggestion.domain.split('.').slice(1).join('.').toLowerCase();

        if (!suggestionsByTld[suggestionTld]) {
          suggestionsByTld[suggestionTld] = [];
        }
        suggestionsByTld[suggestionTld].push(suggestion);
      }

      // Get all available TLDs
      const availableTldsList = Object.keys(suggestionsByTld);

      // Shuffle TLDs to get random variety (not always from top)
      const shuffledTlds = [...availableTldsList].sort(() => Math.random() - 0.5);

      const uniqueSuggestions = [];
      const seenBases = new Set();
      const seenTlds = new Set();

      // First pass: Get one suggestion from each TLD (randomized order)
      for (const tld of shuffledTlds) {
        if (uniqueSuggestions.length >= 6) break;

        const suggestionsForTld = suggestionsByTld[tld];
        if (!suggestionsForTld || suggestionsForTld.length === 0) continue;

        // Shuffle suggestions for this TLD and pick one with different base name
        const shuffledSuggestions = [...suggestionsForTld].sort(() => Math.random() - 0.5);

        for (const suggestion of shuffledSuggestions) {
          const suggestionBase = suggestion.domain.split('.')[0].toLowerCase();

          // Prefer different base names
          if (!seenBases.has(suggestionBase)) {
            seenBases.add(suggestionBase);
            seenTlds.add(tld);
            uniqueSuggestions.push(suggestion);
            break; // Move to next TLD
          }
        }
      }

      // Second pass: If we still don't have 6, allow same base name but different TLD
      if (uniqueSuggestions.length < 6) {
        for (const tld of shuffledTlds) {
          if (uniqueSuggestions.length >= 6) break;
          if (seenTlds.has(tld)) continue; // TLD already used

          const suggestionsForTld = suggestionsByTld[tld];
          if (!suggestionsForTld || suggestionsForTld.length === 0) continue;

          // Shuffle and pick any available suggestion
          const shuffledSuggestions = [...suggestionsForTld].sort(() => Math.random() - 0.5);

          for (const suggestion of shuffledSuggestions) {
            const suggestionBase = suggestion.domain.split('.')[0].toLowerCase();

            // Base name should be different from original
            if (suggestionBase !== baseNameLower) {
              seenTlds.add(tld);
              uniqueSuggestions.push(suggestion);
              break; // Move to next TLD
            }
          }
        }
      }

      // Third pass: If still less than 6, be more lenient but maintain TLD diversity
      if (uniqueSuggestions.length < 6) {
        // Get all remaining suggestions with unique TLDs
        const remainingSuggestions = [];
        for (const tld of shuffledTlds) {
          if (seenTlds.has(tld)) continue;

          const suggestionsForTld = suggestionsByTld[tld];
          if (suggestionsForTld && suggestionsForTld.length > 0) {
            // Shuffle and take first one
            const shuffled = [...suggestionsForTld].sort(() => Math.random() - 0.5);
            const suggestion = shuffled[0];
            const suggestionBase = suggestion.domain.split('.')[0].toLowerCase();

            if (suggestionBase !== baseNameLower) {
              remainingSuggestions.push(suggestion);
              seenTlds.add(tld);
              if (remainingSuggestions.length + uniqueSuggestions.length >= 6) break;
            }
          }
        }

        uniqueSuggestions.push(...remainingSuggestions);
      }

      // If we have less than 6, try to get more from mappedSuggestions
      if (uniqueSuggestions.length < 6 && mappedSuggestions.length > uniqueSuggestions.length) {
        // Get more suggestions that haven't been added yet
        const remainingMapped = mappedSuggestions.filter(s => {
          const sDomain = s.domain.toLowerCase();
          return !uniqueSuggestions.some(u => u.domain.toLowerCase() === sDomain);
        });

        // Add more until we have at least 6 or run out
        for (const suggestion of remainingMapped) {
          if (uniqueSuggestions.length >= 6) break;
          uniqueSuggestions.push(suggestion);
        }
      }

      // Validate exactly 6 suggestions to optimize API calls
      // Only fetch 6 domains at a time to minimize API calls
      const suggestionsToValidate = uniqueSuggestions.slice(0, 6).sort(() => Math.random() - 0.5);

      // Validate availability with find-irsfa.php before showing
      if (suggestionsToValidate.length > 0) {
        let validatedSuggestions = await validateDomainAvailability(suggestionsToValidate);

        // If we don't have enough validated suggestions (less than 6), fetch exactly what we need
        if (validatedSuggestions.length < 6 && mappedSuggestions.length > suggestionsToValidate.length) {
          // Get more suggestions that haven't been validated yet
          const remainingMapped = mappedSuggestions.filter(s => {
            const sDomain = s.domain.toLowerCase();
            return !suggestionsToValidate.some(v => v.domain.toLowerCase() === sDomain);
          });

          // Shuffle and take exactly what we need (6 - current validated count)
          const shuffledRemaining = [...remainingMapped].sort(() => Math.random() - 0.5);
          const neededCount = 6 - validatedSuggestions.length;
          const additionalToValidate = shuffledRemaining.slice(0, neededCount);

          if (additionalToValidate.length > 0) {
            const additionalValidated = await validateDomainAvailability(additionalToValidate);
            validatedSuggestions.push(...additionalValidated);
          }
        }

        // After validation, ensure TLD diversity is maintained but MUST have 6 suggestions
        const finalSuggestions = [];
        const finalSeenTlds = new Set();

        // Shuffle validated suggestions for variety
        const shuffledValidated = [...validatedSuggestions].sort(() => Math.random() - 0.5);

        // First pass: Prioritize suggestions with unique TLDs
        for (const suggestion of shuffledValidated) {
          if (finalSuggestions.length >= 6) break;

          const suggestionTld = suggestion.domain.split('.').slice(1).join('.').toLowerCase();

          // Only add if TLD is different
          if (!finalSeenTlds.has(suggestionTld)) {
            finalSeenTlds.add(suggestionTld);
            finalSuggestions.push(suggestion);
          }
        }

        // Second pass: If we still don't have 6, allow duplicate TLDs but prioritize diversity
        if (finalSuggestions.length < 6) {
          for (const suggestion of shuffledValidated) {
            if (finalSuggestions.length >= 6) break;

            // Check if already added
            if (!finalSuggestions.find(s => s.domain === suggestion.domain)) {
              const suggestionTld = suggestion.domain.split('.').slice(1).join('.').toLowerCase();

              // Count how many suggestions already have this TLD
              const tldCount = finalSuggestions.filter(s =>
                s.domain.split('.').slice(1).join('.').toLowerCase() === suggestionTld
              ).length;

              // Allow if TLD count is less than 3 (max 3 per TLD if needed to reach 6)
              if (tldCount < 3) {
                finalSuggestions.push(suggestion);
              }
            }
          }
        }

        // Third pass: If STILL less than 6, add any remaining validated suggestions (ignore TLD diversity)
        if (finalSuggestions.length < 6) {
          for (const suggestion of shuffledValidated) {
            if (finalSuggestions.length >= 6) break;

            // Check if already added
            if (!finalSuggestions.find(s => s.domain === suggestion.domain)) {
              finalSuggestions.push(suggestion);
            }
          }
        }

        // Final shuffle for display variety - MUST have 6
        const finalShuffled = finalSuggestions.slice(0, 6).sort(() => Math.random() - 0.5);

        if (finalShuffled.length > 0) {
          setSuggestions(finalShuffled);

          // Fetch prices for all suggestions
          await fetchDomainPrices(finalShuffled);

          if (finalShuffled.length < 6) {
            console.warn(`Only ${finalShuffled.length} suggestions available after validation (target: 6)`);
          }
        } else {
          notifyInfo("Tidak ada alternatif domain yang tersedia. Semua domain sudah terdaftar.");
        }
      } else {
        // If no unique suggestions, try to get more from original list
        if (mappedSuggestions.length > 0) {
          // Shuffle and take random suggestions (not just first 10)
          const shuffledMapped = [...mappedSuggestions].sort(() => Math.random() - 0.5);
          // Take random suggestions (up to available length or 20, whichever is smaller)
          const maxFallback = Math.min(shuffledMapped.length, 20);
          const fallbackSuggestions = shuffledMapped.slice(0, maxFallback);
          const validatedFallback = await validateDomainAvailability(fallbackSuggestions);

          if (validatedFallback.length > 0) {
            // Shuffle validated fallback and take 6 random ones
            const shuffledValidated = [...validatedFallback].sort(() => Math.random() - 0.5);
            const finalFallback = shuffledValidated.slice(0, 6);
            setSuggestions(finalFallback);

            // Fetch prices for fallback suggestions
            await fetchDomainPrices(finalFallback);

            notifyInfo(`${finalFallback.length} alternatif domain tersedia ditemukan!`);
          } else {
            notifyInfo("Tidak ada alternatif domain yang ditemukan dengan kata yang berbeda.");
          }
        } else {
          notifyInfo("Tidak ada alternatif domain yang ditemukan dengan kata yang berbeda.");
        }
      }
    } catch (error) {
      console.error("Error generating suggestions:", error);
      notifyError("Gagal mencari alternatif domain. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const calculateSimilarity = (str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    if (longer.length === 0) return 1.0;
    const distance = levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  };

  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };


  // Fetch domain prices for suggestions - OPTIMIZED with parallel fetching
  const fetchDomainPrices = async (suggestionsList) => {
    const pricesMap = {};

    // Get unique extensions to minimize API calls
    const extensionsList = [...new Set(suggestionsList.map(s => {
      const domain = s.domain || s;
      const domainStr = typeof domain === 'string' ? domain : domain.domain;
      return domainStr.substring(domainStr.indexOf("."));
    }))];

    // Fetch all extension prices in parallel
    const pricePromises = extensionsList.map(async (extension) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_API}order/apis/get-price-domain.php?tipedomain=domainregister&extension=${extension}`
        );
        const data = await response.json();

        if (data.message === "success" && data.data && data.data.length > 0) {
          return { extension, prices: data.data[0] };
        }
        return { extension, prices: null };
      } catch (error) {
        console.error(`Error fetching price for extension ${extension}:`, error);
        return { extension, prices: null };
      }
    });

    // Wait for all price fetches to complete in parallel
    const priceResults = await Promise.all(pricePromises);

    // Process all results
    priceResults.forEach(({ extension, prices }) => {
      if (prices) {
        const price = parseFloat(prices.msetupfee || prices.annually || 0);

        // Store price for all domains with this extension
        suggestionsList.forEach(suggestion => {
          const domain = suggestion.domain || suggestion;
          const domainStr = typeof domain === 'string' ? domain : domain.domain;
          if (domainStr.endsWith(extension)) {
            pricesMap[domainStr] = price;
            // Set default duration to 1 for each suggestion
            if (!durations[domainStr]) {
              setDurations(prev => ({ ...prev, [domainStr]: "1" }));
            }
          }
        });

        // Store extension prices for duration options
        if (!extensionPrices[extension]) {
          setExtensionPrices((prev) => ({
            ...prev,
            [extension]: prices,
          }));
        }
      }
    });

    // Fetch TLD promo data for all extensions in parallel (if needed)
    if (extensionsList.length > 0) {
      // Fetch TLD promo data in parallel
      try {
        const extensionsListStr = extensionsList.join(',');
        const promoResponse = await fetch(
          `${process.env.REACT_APP_BASE_API}apis/getTLDPromo.php?ext=${extensionsListStr}`
        );
        const promoData = await promoResponse.json();

        // Convert array to object for easier lookup
        const promoMap = {};
        if (Array.isArray(promoData)) {
          promoData.forEach(item => {
            promoMap[item.extension] = {
              harganormal: parseFloat(item.harganormal.replace(/\./g, '')),
              hargapromo: parseFloat(item.hargapromo.replace(/\./g, ''))
            };
          });
        }
        setTldPromoData(promoMap);
      } catch (error) {
        console.error("Error fetching TLD promo data:", error);
      }
    }

    // Update prices state
    setDomainPrices(prev => ({ ...prev, ...pricesMap }));
  };

  // Render duration options for a domain
  const renderDurationOptions = (domain) => {
    const extension = domain.substring(domain.indexOf("."));
    const prices = extensionPrices[extension];

    if (!prices) return null;

    // Special case for .ai domains
    if (extension === ".ai") {
      return (
        <option key="2" value="2">
          2 Tahun
        </option>
      );
    }

    const priceMap = {
      1: prices.msetupfee,
      2: prices.qsetupfee,
      3: prices.ssetupfee,
      4: prices.asetupfee,
      5: prices.bsetupfee,
      6: prices.monthly,
      7: prices.quarterly,
      8: prices.semiannually,
      9: prices.annually,
      10: prices.biennially,
    };

    // Only show options if triennially is "0.00" and the price is not "-1"
    if (prices.triennially !== "0.00") return null;

    return Object.entries(priceMap).map(([years, price]) => {
      if (price === "-1.00" || price === "-1") return null;
      return (
        <option key={years} value={years}>
          {years} Tahun
        </option>
      );
    });
  };

  // Calculate price based on duration
  const calculatePrice = (domain, selectedDuration) => {
    const extension = domain.substring(domain.indexOf("."));
    const prices = extensionPrices[extension];
    const tldPromo = tldPromoData[extension];

    if (!prices) return domainPrices[domain] || 0;

    const priceMap = {
      1: prices?.msetupfee,
      2: prices?.qsetupfee,
      3: prices?.ssetupfee,
      4: prices?.asetupfee,
      5: prices?.bsetupfee,
      6: prices?.monthly,
      7: prices?.quarterly,
      8: prices?.semiannually,
      9: prices?.annually,
      10: prices?.biennially,
    };

    const priceFromMap = parseFloat(priceMap[selectedDuration] || 0);

    // Check if domain should be free
    const isFree = getIsFreeDomainHelper(domain, selectedDuration, cartItems);
    const hasFreeDomainInCartCheck = hasFreeDomainInCart(cartItems);

    if (isFree && !hasFreeDomainInCartCheck) {
      return 0;
    }

    // Handle .ai domain special case
    if (domain.endsWith(".ai")) {
      const twoYearPrice = parseFloat(prices?.qsetupfee || priceFromMap || 0);
      return tldPromo ? tldPromo.hargapromo : twoYearPrice;
    }

    // Handle regular domain pricing for duration > 1
    if (parseInt(selectedDuration, 10) > 1) {
      return priceFromMap;
    }

    // Default case for 1-year domains - use TLD promo if available
    if (selectedDuration === "1" && tldPromo) {
      return tldPromo.hargapromo;
    }

    // Fallback
    return priceFromMap || domainPrices[domain] || 0;
  };

  // Validate domain availability using the same API as domain search (fetchSearch)
  // OPTIMIZED: Fetch all domains in parallel instead of sequential
  const validateDomainAvailability = async (suggestions) => {
    // Create promises for all domain checks in parallel
    const validationPromises = suggestions.map(async (suggestion) => {
      try {
        // Use the same API endpoint as domain search (find-irsfa.php)
        const checkResponse = await fetch(
          `https://qwords.com/apis/find-irsfa.php?domain=${suggestion.domain}`
        );

        if (!checkResponse.ok) {
          console.error(`HTTP error checking ${suggestion.domain}:`, checkResponse.status);
          return { suggestion, isAvailable: false };
        }

        const checkData = await checkResponse.json();

        // Parse response format from find-irsfa.php
        // Response format: { datas: { data: { result: { domainStatus: "available" | "not-available" } } } }
        let isAvailable = false;

        // Check the correct path based on domainsSlice.js parsing
        if (checkData.datas?.data?.result?.domainStatus === "available") {
          isAvailable = true;
        } else if (checkData.datas?.data?.result?.domainStatus === "Available") {
          isAvailable = true;
        }

        if (!isAvailable) {
          console.log(`Domain ${suggestion.domain} is not available:`, checkData);
        }

        return { suggestion, isAvailable };
      } catch (error) {
        console.error(`Error checking availability for ${suggestion.domain}:`, error);
        // Skip this domain if check fails
        return { suggestion, isAvailable: false };
      }
    });

    // Wait for all validations to complete in parallel
    const validationResults = await Promise.all(validationPromises);

    // Filter only available domains
    const validatedSuggestions = validationResults
      .filter(result => result.isAvailable)
      .map(result => result.suggestion);

    return validatedSuggestions;
  };

  const handleUseDomain = async (suggestion) => {
    const domain = suggestion.domain;

    try {
      // Check if original domain (unavailableDomain) is available
      const originalDomainResult = results?.find(r => r.domain === unavailableDomain);
      const isOriginalDomainAvailable = originalDomainResult?.status === "available";

      // Extract domain name and extension
      const domainParts = domain.split('.');
      const domainName = domainParts[0];
      const extension = '.' + domainParts.slice(1).join('.');

      // Get selected duration for this domain
      const selectedDuration = durations[domain] || "1";

      // Calculate price based on selected duration
      const finalPrice = calculatePrice(domain, selectedDuration);

      // Condition 1: If original domain is available, add suggestion directly to cart
      if (isOriginalDomainAvailable) {
        // Check if domain already in cart
        const existingDomain = cartItems.find((item) => item.domain === domain);
        if (existingDomain) {
          notifyError("Domain ini sudah ada di keranjang.");
          return;
        }

        // Add directly to cart
        dispatch(
          addDomainToCart({
            name: domain,
            status: "available",
            domain: domain,
            price: finalPrice,
            priceRenew: finalPrice,
            type: "register",
            duration: selectedDuration,
            isWhois: false,
            freeWhois: false,
          })
        );
        notifySuccess(`Domain ${domain} berhasil ditambahkan ke keranjang!`);
        // notifyInfo(`Domain ${domain} telah ditambahkan ke keranjang.`);
      } else {
        // Condition 2: If original domain is not-available, trigger search to show whois and bundle options
        const priceString = String(finalPrice);
        dispatch(search({ domain: domainName, extension: extension, price: priceString }));
        dispatch(searchDomainName(domain));
        notifyInfo(`Mengecek domain ${domain}...`);
      }
    } catch (error) {
      console.error("Error using domain:", error);
      notifyError("Terjadi kesalahan saat menggunakan domain ini.");
    }
  };

  if (!unavailableDomain) {
    return null;
  }

  return (
    <div className="mt-4">
      {loading ? (
        <div className="text-center py-4">
          <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-warning" style={{ fontSize: '1.5rem' }} />
          <div className="mt-3">
            <span className="text-muted">Mencari alternatif domain untuk "{unavailableDomain}"...</span>
          </div>
        </div>
      ) : suggestions.length > 0 ? (
        <>
          <div
            className="d-flex justify-content-between align-items-center mb-3"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowSuggestions(!showSuggestions)}
          >
            <h6 className="fw-bold mb-0 domain-suggestions-title">
              Domain Tersedia Lainnya ({suggestions.length})
            </h6>
            <button
              type="button"
              className="btn btn-link p-0 domain-suggestions-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowSuggestions(!showSuggestions);
              }}
              style={{
                textDecoration: 'none',
                border: 'none',
                background: 'none',
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon
                icon={showSuggestions ? faChevronUp : faChevronDown}
                className="ms-2"
              />
            </button>
          </div>
          {showSuggestions && (
            <div className="row g-2">
              {suggestions.map((suggestion, index) => {
                const selectedDuration = durations[suggestion.domain] || "1";
                const extension = suggestion.domain.substring(suggestion.domain.indexOf("."));
                const prices = extensionPrices[extension];
                const tldPromo = tldPromoData[extension];
                const currentPrice = calculatePrice(suggestion.domain, selectedDuration);
                const isFree = getIsFreeDomainHelper(suggestion.domain, selectedDuration, cartItems);
                const hasFreeDomainInCartCheck = hasFreeDomainInCart(cartItems);

                return (
                  <div key={index} className="col-12 mb-2">
                    <div
                      className="border rounded p-2 p-md-3 domain-suggestion-card"
                      style={{
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div className="row g-2 align-items-center">
                        {/* Domain Name */}
                        <div className="col-12 col-md-4">
                          <p className="mb-0 fw-bold domain-suggestion-name" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>
                            {suggestion.domain}
                          </p>
                        </div>

                        {/* Duration Dropdown */}
                        <div className="col-6 col-md-2">
                          <select
                            className="form-select form-select-sm"
                            style={{ fontSize: 'clamp(12px, 1.5vw, 14px)' }}
                            value={selectedDuration}
                            onChange={(e) => {
                              setDurations(prev => ({
                                ...prev,
                                [suggestion.domain]: e.target.value
                              }));
                            }}
                            disabled={!prices || (prices.triennially !== "0.00")}
                          >
                            {renderDurationOptions(suggestion.domain) || (
                              <option value="1">1 Tahun</option>
                            )}
                          </select>
                        </div>

                        {/* Price */}
                        <div className="col-6 col-md-3 text-end text-md-end">
                          {(() => {
                            if (isFree && !hasFreeDomainInCartCheck) {
                              return (
                                <div className="d-flex flex-column align-items-end">
                                  <span className="text-muted small text-decoration-line-through fw-bold">
                                    {formatIDR(domainPrices[suggestion.domain] || 0)}
                                  </span>
                                  <p className="mb-0 fw-bold domain-suggestion-price" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>
                                    {formatIDR(0)}
                                  </p>
                                  <span className="badge bg-success text-white mt-1" style={{ fontSize: '10px', padding: '2px 6px' }}>
                                    GRATIS
                                  </span>
                                </div>
                              );
                            }

                            if (tldPromo && tldPromo.harganormal !== tldPromo.hargapromo && selectedDuration === "1") {
                              return (
                                <div className="d-flex flex-column align-items-end">
                                  <span className="text-muted small text-decoration-line-through fw-bold">
                                    {formatIDR(tldPromo.harganormal)}
                                  </span>
                                  <p className="mb-0 fw-bold domain-suggestion-price" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>
                                    {formatIDR(tldPromo.hargapromo)}
                                  </p>
                                </div>
                              );
                            }

                            return (
                              <p className="mb-0 fw-bold text-end domain-suggestion-price" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>
                                {formatIDR(currentPrice)}
                              </p>
                            );
                          })()}
                        </div>

                        {/* Add Button */}
                        <div className="col-12 col-md-3 text-end text-md-end d-flex justify-content-end justify-content-md-end">
                          <Button
                            size="sm"
                            className="fw-bold d-flex align-items-center justify-content-center"
                            style={{
                              backgroundColor: '#ee4d30',
                              borderColor: '#ee4d30',
                              color: '#fff',
                              borderRadius: '6px',
                              width: '40px',
                              height: '40px',
                              padding: '0',
                              minWidth: '40px'
                            }}
                            onClick={() => handleUseDomain(suggestion)}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default DomainSuggestions;
